import {
  AttributeSchema,
  PIMProduct,
  ProductFormSchema,
  ProductReference,
  TemplateProductFormStepItemAttributeSchema,
  TemplateProductFormStepItemSchema,
  TemplateProductFormStepSchema,
} from "../../../../../types/types.PIM";
import { DocumentsOrDigitalMediaTable } from "../../../ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { useTranslation } from "react-i18next";
import { InnerTitleText } from "../../../../../components/Typography/Typography";
import { WhiteInnerCard } from "../../../../../layout/portalPageLayout";
import styled, { useTheme } from "styled-components/macro";
import { TooltipPortal } from "../../../../../components/TooltipModal/TooltipPortal";
import { AttributeValueDisplay } from "../../../ProductDetailPage/ProductOverview/product_overview.utils";
import ReactTooltip from "react-tooltip";
import { CollectionComponent } from "../../../ProductDetailPage/Collections/CollectionComponent";
import { AccordionCardWithEditFeatures } from "../../../../../components/AccordionCard/AccordionCardwithEditFeatures";
import { Dispatch, SetStateAction, useMemo } from "react";
import { useAuthContext } from "../../../../../components/Auth";
import { screenSize } from "../../../../../theme";
import useSWR from "swr";
import { makeUrlWithParams, useStoreState } from "../../../../../util/util";
import { endpoints } from "../../../../../endpoints";
import { Loader } from "../../../../../components/Loader/Loader";
import { PROFILE_DETAIL_CLASSES } from "../../../../../layout/shared/ProfileDetailLayout";

const ReviewCardContainer = styled(WhiteInnerCard)`
  border-color: ${({ theme }) => theme.secondaryBorder};
  .accordion-content-wrapper {
    padding-bottom: 0;
    border-bottom: none;
  }
  .${PROFILE_DETAIL_CLASSES.assetsTable} > div {
    margin-bottom: 0;
  }
  div[class*="ProductCollectionSchemaTable"] {
    margin-bottom: 0;
  }
`;

const StepContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  .${PROFILE_DETAIL_CLASSES.assetsTable} {
    width: 100%;
  }
  ul:not(.multiselect_tooltip_list):not(.object_reference_list) {
    list-style: none;
    padding-left: 0;
    margin-left: 0;
    li:not(.multiselect_tooltip_list_item) {
      list-style: none;
      margin: 0 ${({ theme }) => theme.resolve("spacing_md")}
        ${({ theme }) => theme.resolve("spacing_md")} 0;
      font-size: ${({ theme }) => theme.resolve("font_size_sm")};
      line-height: ${({ theme }) => theme.resolve("line_height_sm")};
      color: ${({ theme }) => theme.resolve("color_text_primary")};
      display: inline-block;
      border: ${({ theme }) => theme.resolve("border_md")} solid
        ${({ theme }) => theme.resolve("color_border_lighter")};
      border-radius: ${({ theme }) => theme.resolve("radius_xs")};
      padding: ${({ theme }) => theme.resolve("spacing_xs")}
        ${({ theme }) => theme.resolve("spacing_sm")};
    }
  }
`;

const AttributeItemWrapper = styled.div<{ shouldFullSpan: boolean }>`
  width: ${({ shouldFullSpan }) => (shouldFullSpan ? `100%` : `50%`)};
  height: max-content;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  @media ${screenSize.medium} {
    width: 100%;
  }
`;

const modifyProdrefDescription = (
  prod_ref: ProductReference
): ProductReference => ({
  ...prod_ref,
  product_description: prod_ref.product_description
    ?.replace(/<ul/g, '<ul class="multiselect_tooltip_list"')
    .replace(/<li/g, '<li class="multiselect_tooltip_list_item"'),
});

const AttributeContent = ({ attribute }: { attribute: AttributeSchema }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const modifiedAttribute = useMemo(() => {
    if (attribute.input_type === "product_reference") {
      return {
        ...attribute,
        values: attribute.values?.map((val) => ({
          ...val,
          product_ref: val.product_ref
            ? modifyProdrefDescription(val.product_ref)
            : undefined,
        })),
      };
    }
    return attribute;
  }, [attribute]);

  const shouldFullSpan =
    attribute.input_type === "product_reference" &&
    !!attribute.is_multiple_choice &&
    (attribute.values?.length ?? 0) > 2;
  return (
    <AttributeItemWrapper shouldFullSpan={shouldFullSpan}>
      <InnerTitleText
        style={{ textAlign: "left", display: "inline-block" }}
        data-for={attribute?.id}
        data-tip={attribute?.description ? attribute?.description : undefined}
      >
        {attribute?.display_name
          ? t([attribute?.display_name])
          : attribute?.name}
      </InnerTitleText>
      <AttributeValueDisplay
        attribute={modifiedAttribute}
        isPortfolio={false}
      />
      {attribute?.description?.replace(/\s/g, "") ? (
        <TooltipPortal>
          <ReactTooltip
            id={attribute?.id}
            place="top"
            data-html={true}
            effect="solid"
            backgroundColor={theme.tooltipBG}
            multiline={true}
          />
        </TooltipPortal>
      ) : (
        <></>
      )}
    </AttributeItemWrapper>
  );
};

/**
 * Combines values from a form step item attribute and its related group attribute.
 *
 * If the attribute has its own 'values' array and it is not empty, these values will override
 * the groupAttribute.values. Otherwise, the values from groupAttribute are used.
 *
 * @param attribute - An attribute from TemplateProductFormStepItemAttributeSchema containing possible override values.
 * @param groupAttribute - The AttributeSchema representing the base attribute to combine with.
 * @returns The AttributeSchema with values combined according to the above logic or null if parameters are missing.
 */
const combineAttributesValues = (
  attribute: TemplateProductFormStepItemAttributeSchema,
  groupAttribute?: AttributeSchema
): AttributeSchema | null => {
  if (!attribute || !groupAttribute) return null;
  return {
    ...groupAttribute,
    values:
      !!attribute.values && attribute.values.length > 0
        ? attribute.values
        : groupAttribute.values,
  };
};

export const ReviewStep = ({
  formData,
  productId,
  setCurrentStep,
  formStatus,
  setManualNavigation,
}: {
  formData: ProductFormSchema;
  productId: string;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  formSubmitted: boolean;
  setManualNavigation: Dispatch<SetStateAction<boolean>>;
  formStatus: "submitted" | "discarded" | "pending" | "completed";
}) => {
  const { tenant_id } = useStoreState();
  const { user } = useAuthContext();
  const { data: productData, mutate: mutateProductData } = useSWR<PIMProduct>(
    productId
      ? makeUrlWithParams(
          endpoints.v2_tenants_id_pim_products_id(tenant_id, productId),
          {
            use_database: "true",
          }
        )
      : null,
    {
      revalidateOnFocus: false,
    }
  );

  return !productData ? (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Loader omitContainer={true} isLoading={!productData} />
    </div>
  ) : (
    <div>
      {formData?.form?.steps?.map(
        (step: TemplateProductFormStepSchema, index: number) => (
          <ReviewCardContainer key={step.id}>
            <AccordionCardWithEditFeatures
              title={step.name}
              handleEdit={
                formStatus !== "pending" ||
                user?.email_address !== formData?.user?.email_address
                  ? undefined
                  : () => {
                      setManualNavigation(true);
                      setCurrentStep(index);
                    }
              }
              expanded={true}
              editing={false}
            >
              <StepContainer>
                {step.is_document_step ? (
                  <DocumentsOrDigitalMediaTable
                    title={"Documents"}
                    product={productData}
                    fetchProductData={mutateProductData}
                    replaceProductId={() => {}}
                    isOutsideLogin={true}
                    isShowTableWithBulkAction={false}
                    isShowGridAssetWithBulkAction={false}
                    hideViewToggle={true}
                    hideFilters={true}
                    showSearch={true}
                    hideAddButton={true}
                    uploadNewDocumentsOnly={true}
                    hideAccessibility={true}
                    hideHeader={true}
                  />
                ) : (
                  <>
                    {step.items?.map(
                      (item: TemplateProductFormStepItemSchema) => {
                        let collection =
                          productData.product_schema.collections.find(
                            (collection) =>
                              collection.id === item.group_or_collection_id
                          );
                        const newColumns = collection?.columns.filter(
                          (column) =>
                            item.attributes.some(
                              (attribute) =>
                                attribute.attribute_id === column.id ||
                                attribute.display_name === column.display_name
                            )
                        );
                        collection = { ...collection!, columns: newColumns! };
                        const attribute = combineAttributesValues(
                          item.attributes[0],
                          productData?.product_schema.groups
                            ?.find(
                              (group) =>
                                group.id === item.group_or_collection_id
                            )
                            ?.attributes?.find(
                              (attribute) =>
                                attribute.id === item.attributes[0].attribute_id
                            )
                        );
                        return item.type === "group_attribute" ? (
                          <>
                            {attribute ? (
                              <AttributeContent
                                key={item.attributes[0].attribute_id}
                                attribute={attribute}
                              />
                            ) : (
                              <></>
                            )}
                          </>
                        ) : collection ? (
                          <div style={{ width: "100%" }}>
                            <CollectionComponent
                              key={collection.id}
                              collection={collection}
                              product={productData}
                              fetchProductData={mutateProductData}
                              replaceProductId={() => {}}
                              isPortfolio={true}
                              isProductEntry={true}
                            />
                          </div>
                        ) : null;
                      }
                    )}
                  </>
                )}
              </StepContainer>
            </AccordionCardWithEditFeatures>
          </ReviewCardContainer>
        )
      )}
    </div>
  );
};
